import { useLayoutEffect, useRef } from 'react';
import { gsap, Linear } from 'gsap';
import ScrollMagic from 'scrollmagic';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import { useIsMounted } from '@hooks/useIsMounted';
import { useThemeMediaQuery } from '@hooks/useThemeMediaQuery';

export const useBrowserImageController = () => {
  const scrollController = useScrollController();
  const isMounted = useIsMounted();
  const browserImageRef = useRef<HTMLDivElement>(null);
  const browserImageEle = browserImageRef.current;
  const isMobile = useThemeMediaQuery('below_sm');
  useLayoutEffect(() => {
    let scene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let heroTimeline: ReturnType<typeof gsap.timeline>;

    if (scrollController && isMounted) {
      const initTimeline = () => {
        if (scrollController && browserImageEle) {
          heroTimeline = gsap.timeline({ paused: true, defaults: { ease: Linear.easeNone } });
          heroTimeline.fromTo(
            browserImageEle,
            {
              transform: 'translateY(0px)',
            },
            {
              transform: isMobile ? 'translateY(-650px)' : 'translateY(-1150px)',
            },
            0
          );
        }
      };
      const initScene = () => {
        scene = new ScrollMagic.Scene({
          triggerElement: '#browser-image-wrapper-v2',
          triggerHook: 'onLeave',
          duration: '150%',
          offset: isMobile ? 0 : -400,
        })
          .addTo(scrollController)
          // workaround from here https://github.com/janpaepke/ScrollMagic/issues/918 until plugin for gsap 3 released https://github.com/janpaepke/ScrollMagic/pull/920 or we update it locally
          .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
            heroTimeline?.progress(event.progress);
          });
      };
      initTimeline();
      initScene();
    }
    const clearScene = () => {
      if (heroTimeline) {
        heroTimeline.progress(0);
        heroTimeline.kill();
      }
      if (scene) {
        scene.destroy(true);
      }
    };
    return () => {
      clearScene();
    };
  }, [scrollController, isMounted, isMobile, browserImageEle]);
  return { browserImageRef };
};
